import { Apps as MenuIcon } from '@mui/icons-material';
import { Alert, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeaderBanner from '../components/PageHeaderBanner';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { MENU_MODE, staticMenuItems, staticMenuToDbFormat } from '../config/menuConfig';
import { visibleMenuItems } from '../domain/menuVisibility';

interface MenuItem {
    Id: number;
    Name: string;
    Parent: number;
    Enabled: number;
    Level: number;
    Hasc: number;
    Visible: number;
    Open: number;
    formno: number | null;
    form?: string;
    Button?: string;
}

const MainMenu = () => {
    const { menu } = useApi();
    const { user } = useAuth();
    const role = user?.role;
    const allowedPages = user?.allowedPages;
    const [items, setItems] = useState<MenuItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        const loadMenu = async () => {
            setLoading(true);

            if (MENU_MODE === 'static') {
                // Use static menu configuration, limited to what this user may open
                setItems(
                    staticMenuToDbFormat(visibleMenuItems(staticMenuItems, { role, allowedPages })),
                );
                setLoading(false);
                return;
            }

            // Dynamic mode: load from database
            const result = await menu.getAll();
            if (result.success) {
                // Dynamically derive implemented routes from routeConfig
                const { implementedRouteNames } = await import('../routeConfig');
                const filtered = (result.data ?? []).filter(it => {
                    const raw = it.form ?? it.Button;
                    if (!raw) return false;
                    const form = String(raw).replace(/^\//, '').toLowerCase();
                    return implementedRouteNames.has(form);
                });

                setItems(filtered);
            } else {
                setError(result.error ?? t('mainMenu.error'));
            }
            setLoading(false);
        };
        void loadMenu();
    }, [menu, t, role, allowedPages]);

    const handleClick = (item: MenuItem) => {
        const raw = item.form ?? item.Button;
        if (raw) {
            const path = String(raw).startsWith('/') ? String(raw) : `/${raw}`;
            navigate(path);
        }
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert severity="error">{t('mainMenu.error')}</Alert>;
    }

    return (
        <Box>
            <PageHeaderBanner
                icon={<MenuIcon />}
                title={t('mainMenu.title')}
                subtitle={t('mainMenu.subtitle')}
                accentColor="#0f766e"
            />
            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
                {items.map(it => (
                    <Card
                        key={it.Id}
                        sx={{
                            cursor: 'pointer',
                            textAlign: 'center',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                        onClick={() => handleClick(it)}
                    >
                        <CardContent>
                            <Typography variant="h6">{it.Name}</Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default MainMenu;
